import { useQuery } from '@tanstack/react-query'

import { consulta, ErrorDeLaApi, fetchConSesion, mensajePorEstado, pedir } from './nucleo'

/**
 * El importador de productos (docs/alta-de-web-con-plantillas.md §7.3): la plantilla,
 * analizar un Excel (columnas, muestra y mapeo propuesto), importar con el mapeo
 * confirmado, y publicar de golpe. Lo que se puede importar lo decide el núcleo.
 *
 * Importar es un **trabajo en cola**: el núcleo lo acepta enseguida y devuelve su id, y
 * el panel pregunta cómo va hasta que termina. Con imágenes de verdad, esperar la
 * respuesta pasaba de los 100 s que da Cloudflare, y reintentar duplicaba productos.
 * Pedir la misma importación otra vez (el mismo fichero y el mismo mapeo) devuelve el
 * trabajo que ya hay; importarla **otra vez** de verdad se pide con `repetir`.
 */
/** `formato`: una columna de un formato conocido (WooCommerce) con su destino fijo; si no se importa, `motivo` dice por qué. */
export type Mapeo = { columna: string; indice: number; campo: null | string; confianza: 'exacta' | 'formato' | 'ia' | 'ninguna' | 'sinonimo' | 'valores'; motivo?: string }
export type CampoImportable = { clave: string; etiqueta: string; nota: string; obligatoria: boolean }
/** El formato que reconoce el núcleo por las cabeceras: una exportación de WooCommerce, o una hoja cualquiera. */
export type FormatoDeHoja = 'generico' | 'woocommerce'
export type Analisis = {
  formato?: FormatoDeHoja
  cabeceras: string[]
  totalDeFilas: number
  muestra: string[][]
  mapeo: Mapeo[]
  sugeridasPorIA?: number
  /** Lo que ha costado que la IA proponga las columnas (se cobra como «Importación»). */
  creditosPorIA?: number
  /** Si no se le preguntó a la IA porque no había créditos: lo que se le dice al cliente. */
  iaSinSaldo?: null | string
  campos: CampoImportable[]
  opcion: string
}
/**
 * Un aviso del importador: `texto` es igual para la misma causa en todos los productos
 * (sirve para agruparlos) y `dato` es lo concreto (la categoría, la dirección, la celda).
 * Un informe viejo que quede en el historial del navegador los trae como frases sueltas.
 */
export type AvisoDeImportacion = { texto: string; dato?: string }
export type Informe = {
  formato?: FormatoDeHoja
  notas?: string[]
  creados: Array<{ id: number; nombre: string; slug: string; variantes: number; avisos: Array<AvisoDeImportacion | string> }>
  rechazados: Array<{ filas: number[]; nombre: string; motivo: string }>
  totales: { filas: number; productos: number; creados: number; conAvisos: number; rechazados: number; categoriasCreadas: number; imagenesDescargadas: number; imagenesReutilizadas?: number }
}

export type EstadoDeImportacion = 'en-cola' | 'en-curso' | 'fallida' | 'terminada'

/** Un trabajo de importación, como lo cuenta el núcleo (`GET /api/panel/productos/importacion`). */
export type Importacion = {
  id: number
  sitio: number
  estado: EstadoDeImportacion
  archivo: null | string
  progreso: { fase: 'creando' | 'esperando' | 'leyendo' | 'terminando'; productos: { hechos: number; total: number }; imagenes: { hechas: number; total: number } }
  /** Solo cuando ha terminado. */
  informe: Informe | null
  /** Si falló o se cortó: cuántos productos llegó a crear (se quedan; otro intento sigue desde ahí). */
  creados: number
  error: null | string
  /** «En curso», pero el servidor ha dejado de trabajar en ella (un reinicio): se puede seguir. */
  cortada: boolean
  /** Si «Volver a intentarlo» puede servir; lo decide el núcleo. */
  reintentable: boolean
  /** En cola: cuántas importaciones de la misma cuenta van delante (se hacen de una en una). */
  delante: number
  /** La misma importación ya estaba pedida: es la que había, no una nueva. */
  repetida: boolean
  creadaEn: string
  terminadaEn: null | string
}

/** La última importación de una web, sin el informe: para volver a él desde Productos. */
export type UltimaImportacion = Omit<Importacion, 'informe'> & { totales: Informe['totales'] | null }

export const enMarcha = (estado: EstadoDeImportacion | undefined) => estado === 'en-cola' || estado === 'en-curso'

/** «Creando los productos: 7 de 20 · imágenes 15 de 60». */
export const textoDelProgreso = (i: Pick<Importacion, 'delante' | 'estado' | 'progreso'>): string => {
  if (i.estado === 'en-cola' && i.delante > 0) {
    return `En cola: ${i.delante === 1 ? 'hay otra importación' : `hay ${i.delante} importaciones`} de productos de esta cuenta delante, y se hacen de una en una. Empieza en cuanto ${i.delante === 1 ? 'termine' : 'terminen'}.`
  }
  if (i.estado === 'en-cola' || i.progreso.fase === 'esperando') return 'En cola: empieza en un momento.'
  if (i.progreso.fase === 'leyendo') return 'Leyendo el fichero…'
  if (i.progreso.fase === 'terminando') return 'Terminando…'
  const { productos, imagenes } = i.progreso
  const deImagenes = imagenes.total > 0 ? ` · imágenes ${imagenes.hechas} de ${imagenes.total}` : ''
  return `Creando los productos: ${productos.hechos} de ${productos.total}${deImagenes}`
}

/** Un aviso en una sola frase, venga como venga: «texto (dato)». */
export const textoDelAviso = (aviso: AvisoDeImportacion | string): string => (typeof aviso === 'string' ? aviso : aviso.dato ? `${aviso.texto} (${aviso.dato})` : aviso.texto)

export const urlDeLaPlantilla = (sitio: number) => `/api/panel/productos/plantilla?sitio=${sitio}`
/** Desde el asistente, antes de que exista la web: con el espacio y la moneda elegida. */
export const urlDeLaPlantillaDelEspacio = (cliente: number, moneda: string) => `/api/panel/productos/plantilla?cliente=${cliente}&moneda=${encodeURIComponent(moneda)}`

const enviar = async <T>(ruta: string, archivo: File, campos: Record<string, string>): Promise<T> => {
  const cuerpo = new FormData()
  for (const [clave, valor] of Object.entries(campos)) cuerpo.append(clave, valor)
  cuerpo.append('archivo', archivo, archivo.name)
  const respuesta = await fetchConSesion(`/api${ruta}`, { method: 'POST', credentials: 'same-origin', body: cuerpo })
  const json = (await respuesta.json().catch(() => ({}))) as Record<string, unknown> & { errors?: Array<{ message?: string; data?: Record<string, unknown> }> }
  if (!respuesta.ok) {
    const primero = json.errors?.[0]
    throw new ErrorDeLaApi(primero?.message ?? mensajePorEstado(respuesta.status), respuesta.status, primero?.data ?? {})
  }
  return json as T
}

export const importacion = {
  /** Desde Productos → Importar se manda el sitio; desde el asistente, el espacio (la web aún no existe). */
  analizar: (archivo: File, donde: { sitio: number } | { cliente: number }) =>
    enviar<Analisis>('/panel/productos/analizar', archivo, 'sitio' in donde ? { sitio: String(donde.sitio) } : { cliente: String(donde.cliente) }),
  /** Pone la importación en cola y devuelve el trabajo; el progreso se sigue con `useImportacion`. */
  importar: (sitio: number, archivo: File, mapeo: Mapeo[], o: { repetir?: boolean } = {}) =>
    enviar<Importacion>('/panel/productos/importar', archivo, {
      sitio: String(sitio),
      mapeo: JSON.stringify(mapeo.map(({ indice, columna, campo }) => ({ indice, columna, campo }))),
      ...(o.repetir ? { repetir: '1' } : {}),
    }),
  estado: (sitio: number, id: number) => pedir<Importacion>(`/panel/productos/importacion${consulta({ sitio, id })}`),
  /** Seguir una que falló o se cortó, por su id: el núcleo ya tiene el fichero y el mapeo. */
  reintentar: (sitio: number, id: number) => pedir<Importacion>('/panel/productos/importacion/reintentar', { metodo: 'POST', cuerpo: { sitio, id } }),
  ultima: (sitio: number) => pedir<{ importacion: null | UltimaImportacion }>(`/panel/productos/importacion/ultima${consulta({ sitio })}`),
  publicar: (sitio: number, ids: number[]) => pedir<{ publicados: number[]; fallidos: Array<{ id: number; motivo: string }> }>('/panel/productos/publicar', { metodo: 'POST', cuerpo: { sitio, ids } }),
}

/** Un «no» del núcleo que no va a cambiar por preguntar otra vez: no existe, no es tuyo, no hay sesión. */
export const esRespuestaDefinitiva = (error: unknown): boolean => error instanceof ErrorDeLaApi && error.status >= 400 && error.status < 500

const SONDEO_MS = 1500
const SONDEO_CORTADA_MS = 5000
const SONDEO_MAXIMO_MS = 30_000

/**
 * Cada cuánto se vuelve a preguntar por un trabajo, o `false` para dejar de hacerlo.
 *
 * - Mientras está en marcha, cada segundo y medio; si se ha cortado, más despacio (el
 *   núcleo la relanza solo, o la relanza quien pulse «Seguir»).
 * - Un 5xx o un fallo de red **no** es que la importación haya fallado —un despliegue del
 *   núcleo detrás de Cloudflare da un 502 o un 524 un rato—: se sigue preguntando, cada
 *   vez con más calma, hasta treinta segundos.
 * - Un 4xx sí es definitivo (ya no existe, ya no es tuyo, se acabó la sesión): se para.
 */
export const siguienteSondeo = (o: { datos?: Pick<Importacion, 'cortada' | 'estado'>; error?: unknown; fallosSeguidos: number }): false | number => {
  if (o.error && esRespuestaDefinitiva(o.error)) return false
  if (o.error) return Math.min(SONDEO_MS * 2 ** Math.max(0, o.fallosSeguidos), SONDEO_MAXIMO_MS)
  if (!o.datos) return SONDEO_MS
  if (!enMarcha(o.datos.estado)) return false
  return o.datos.cortada ? SONDEO_CORTADA_MS : SONDEO_MS
}

/** El trabajo, sondeado mientras está en cola o en curso; al terminar (o fallar) deja de preguntar. */
export const useImportacion = (sitio: number, id: null | number) =>
  useQuery({
    queryKey: ['importacion', sitio, id],
    enabled: id !== null,
    queryFn: () => importacion.estado(sitio, id!),
    refetchInterval: (q) => siguienteSondeo({ datos: q.state.data, error: q.state.status === 'error' ? q.state.error : undefined, fallosSeguidos: q.state.fetchFailureCount }),
    // Sin `retry` propio: vale la regla del panel (main.tsx), que no reintenta un 4xx. El
    // sondeo ya vuelve a preguntar ante un 5xx o un fallo de red.
  })

/** La última importación de la web, para el aviso de Productos: se sigue mientras esté en marcha. */
export const useUltimaImportacion = (sitio: number, activo: boolean) =>
  useQuery({
    queryKey: ['importacion', sitio, 'ultima'],
    enabled: activo,
    queryFn: () => importacion.ultima(sitio),
    refetchInterval: (q) => (q.state.status === 'error' ? false : enMarcha(q.state.data?.importacion?.estado) ? SONDEO_CORTADA_MS : false),
  })
